import { CompletablePromise, SemaphorePromise } from './utilities';

/*
  internal type for tracking active areas
 */
type ClickableArea = {
    promise: CompletablePromise,
    hover: ((progress: number) => void) | null,
    mouse_over: boolean,
    x: number,
    y: number,
    w: number,
    h: number,
}

/*
  Internal type for tracking animations, etc
 */
type Ticker = {
    t: number,
    duration: number,
    update: ((progress: number) => void) | null,
    finalise: (() => void),
    dead: boolean,
}

export class ScreenPoint {
    public x: number
    public y: number

    constructor(x: number, y: number) {
        this.x = x
        this.y = y
    }

    distance_to(sp: ScreenPoint): number {
        const dx = this.x - sp.x
        const dy = this.y - sp.y
        return Math.sqrt(dx * dx + dy * dy)
    }

    equal_to(sp: ScreenPoint): boolean {
        return this.x == sp.x && this.y == sp.y
    }
}

export enum PointerButton {
    Main,
    Alt,
}

/*
  Event handling part of the engine
  */
export class EngineBase {
    private el: HTMLElement
    private effective_width: number
    private awaiting_tick: boolean
    private tickers: Ticker[]
    private runner!: Promise<void>
    private clickable_areas: ClickableArea[]
    private onscreen_promise!: CompletablePromise
    private awaiting_scroll_event: boolean
    private below_screen_promise: SemaphorePromise<boolean>
    private last_above_halfway: boolean = false
    private last_tick: number | null = null
    private is_onscreen: boolean = false
    private tick_when_back_onscreen: boolean = false
    private tick_request_count: number = 0

    // stubs
    protected async handle_initialise(): Promise<void> { }
    protected async handle_run(): Promise<void> { }
    protected handle_mouse_down(pt: ScreenPoint, btn: PointerButton): void { }
    protected handle_mouse_move(x: number, y: number): void { }
    protected handle_mouse_up(): void { }
    protected handle_mouse_cancel(): void { this.handle_mouse_up() }
    protected handle_mouse_zoom(x: number, y: number, dz: number): void { }
    protected handle_tick(): void { }

    constructor(el: HTMLElement) {
        this.el = el
        this.effective_width = 10
        this.awaiting_tick = false
        this.tickers = []
        this.clickable_areas = []
        this.awaiting_scroll_event = false
        this.below_screen_promise = new SemaphorePromise<boolean>()
        this.onscreen_promise = new CompletablePromise()
    }

    public name(): string {
        return this.el.id
    }

    // set the declared number of units across for the screen
    protected set_effective_width(units_across: number): void {
        this.effective_width = units_across
        this.geometry_changed()
    }

    // the dom element wrapped by this
    public dom_element(): HTMLElement {
        return this.el
    }

    // This is the width in the same unit system used in set_effective_width
    protected width(): number {
        return this.effective_width
    }

    // the aspect ratio (of the underlying element) w/h
    protected aspect(): number {
        return this.el.offsetWidth / this.el.offsetHeight
    }

    // This is the height in the same unit system used in set_effective_width
    protected height(): number {
        return this.width() / this.aspect()
    }

    protected display_if_needed(): void {

    }

    protected geometry_changed(): void {

    }

    // start calling handle_tick()
    protected start_ticking(): void {
        this.tick_request_count += 1
    }

    // stop calling handle_tick()
    protected stop_ticking(): void {
        this.tick_request_count -= 1
    }

    protected async await_click(x: number, y: number, w: number, h: number, hover: ((progress: number) => void) | null): Promise<void> {
        let ca: ClickableArea = {
            x: x,
            y: y,
            w: w,
            h: h,
            hover: hover,
            mouse_over: false,
            promise: new CompletablePromise(),
        }
        this.clickable_areas.push(ca)
        await ca.promise.wait()
    }

    private tick(dt: number): void {
        this.handle_tick()

        this.tickers.forEach(ticker => {
            ticker.t += dt

            let progress = ticker.t / ticker.duration
            if (progress >= 1.0) {
                progress = 1.0
                ticker.dead = true
            }

            if (ticker.update != null) {
                ticker.update(progress)
            }

            if (ticker.dead && ticker.finalise != null) {
                ticker.finalise()
            }
        })

        this.tickers = this.tickers.filter(item => !item.dead)
    }

    // a method that can be safely be called many times to ensure a tick happens
    private queue_tick_if_required(): boolean {
        if (this.awaiting_tick) {
            return true
        }

        if (!this.is_onscreen) {
            this.tick_when_back_onscreen = true
            this.last_tick = null
            return false
        }

        if (this.tickers.length == 0 && this.tick_request_count == 0) {
            return false
        }

        this.awaiting_tick = true
        window.requestAnimationFrame((timestamp: number) => {
            this.awaiting_tick = false

            var dt:number
            if (this.last_tick == null) {
                this.last_tick = timestamp
                dt = 0
            } else {
                dt = timestamp - this.last_tick
                this.last_tick = timestamp
            }

            this.tick(dt / 1000.0)
            this.display_if_needed()
            if (!this.queue_tick_if_required()) {
                this.last_tick = null
            }
        })
        return true
    }

    private core_animate(duration: number, update: ((progress: number) => void) | null, finalise: (() => void)): void {
        const t: Ticker = {
            t: 0,
            duration: duration,
            update: update,
            finalise: finalise,
            dead: false,
        }
        this.tickers.push(t)
        this.queue_tick_if_required()
    }

    public async await_above_halfway(): Promise<boolean> {
        return await this.below_screen_promise.pull()
    }

    // this waits until it is "substantially" onscreen
    public async await_onscreen(): Promise<void> {
        await this.onscreen_promise.wait()
    }

    public async await_delay(duration: number): Promise<void> {
        let cp = new CompletablePromise()
        this.core_animate(duration, null, () => {
            cp.mark_completed()
        })
        await cp.wait()
    }

    public async await_animate(duration: number, update: ((progress: number) => void)): Promise<void> {
        let cp = new CompletablePromise()
        this.core_animate(duration, update, () => {
            cp.mark_completed()
        })
        await cp.wait()
    }

    private convert_mouse_event(e: MouseEvent): [number, number] {
        return [e.offsetX / this.el.offsetWidth * this.effective_width, e.offsetY / this.el.offsetHeight * this.height()]
    }

    private convert_touch_event(e: TouchEvent): [number, number] {
        const touch = e.touches[0]

        const bounding = this.el.getBoundingClientRect();
        const x = touch.clientX - bounding.left;
        const y = touch.clientY - bounding.top;
        
        return [
            x / this.el.offsetWidth * this.effective_width,
            y / this.el.offsetHeight * this.height()
        ]
    }

    private find_clickable_area(x: number, y: number): ClickableArea | null {
        for (var cai = 0; cai < this.clickable_areas.length; cai += 1) {
            let ca = this.clickable_areas[cai]
            const rx:number = x - ca.x
            const ry:number = y - ca.y

            if (rx < 0 || ry < 0) {
                continue
            }

            if (rx > ca.w || ry > ca.h) {
                continue
            }

            return ca
        }

        return null
    }

    private _handle_mouse_down(x: number, y: number): void {
        let ca = this.find_clickable_area(x, y)
        if (ca == null) {
            this.handle_mouse_down(new ScreenPoint(x, y), PointerButton.Main)
        } else {
            ca.promise.mark_completed()
            this.clickable_areas = this.clickable_areas.filter(ca => {
                if (ca.hover != null) {
                    ca.hover(0)
                }
                !ca.promise.completed()
            })
        }
    }

    private _handle_mouse_zoom(x: number, y: number, dz: number): void {
        this.handle_mouse_zoom(x, y, dz)
    }

    private _handle_mouse_move(x: number, y: number): void {
        let hovered_area = this.find_clickable_area(x, y)
        this.clickable_areas.forEach(ca => {
            let mouse_over = hovered_area === ca
            if (mouse_over != ca.mouse_over) {
                ca.mouse_over = mouse_over
                if (ca.hover != null) {
                    let val = 0
                    if (ca.mouse_over) {
                        val = 1
                    }
                    ca.hover(val)
                }
            }
        })
        this.handle_mouse_move(x, y)
    }

    // a throttled "did scroll"
    private did_scroll(): void {
        const bcr: DOMRect = this.el.getBoundingClientRect()
        const centre_rel_screen = bcr.y + bcr.height * 0.5 - window.innerHeight * 0.5
        const above_halfway: boolean = centre_rel_screen < 0
        if (above_halfway != this.last_above_halfway) {
            this.below_screen_promise.push(above_halfway)
            this.last_above_halfway = above_halfway
        }
    }

    public intercept_pointer_events(): void {
		this.el.addEventListener("touchstart", (e) => {
            e.preventDefault()
            let [x, y] = this.convert_touch_event(e)
            this._handle_mouse_down(x, y)
            this.display_if_needed()
        })

		this.el.addEventListener("touchmove", (e) => {
            e.preventDefault()
            let [x, y] = this.convert_touch_event(e)
            this._handle_mouse_move(x, y)
            this.display_if_needed()
        })

		this.el.addEventListener("touchend", (e) => {
            e.preventDefault()
            this.handle_mouse_up()
            this.display_if_needed()
        })

		this.el.addEventListener("touchcancel", (e) => {
            e.preventDefault()
            this.handle_mouse_cancel()
            this.display_if_needed()
        })

		this.el.addEventListener("mousedown", (e) => {
            let [x, y] = this.convert_mouse_event(e)
            this._handle_mouse_down(x, y)
            this.display_if_needed()
        })

		this.el.addEventListener("wheel", (e) => {
            e.preventDefault()
            let [x, y] = this.convert_mouse_event(e)
            this._handle_mouse_zoom(x, y, e.deltaY)
            this.display_if_needed()
        })

		this.el.addEventListener("mousemove", (e) => {
            let [x, y] = this.convert_mouse_event(e)
            this._handle_mouse_move(x, y)
            this.display_if_needed()
        })

		this.el.addEventListener("mouseup", (e) => {
            this.handle_mouse_up()
            this.display_if_needed()
        })
    }

    private note_onscreen(onscreen: boolean): void {
        if (this.is_onscreen == onscreen) {
            return;
        }

        this.is_onscreen = onscreen

        if (this.is_onscreen && this.tick_when_back_onscreen) {
            this.tick_when_back_onscreen = false
            this.queue_tick_if_required()
        }
    }

    public async setup(): Promise<void> {
        await this.handle_initialise()

        const resizeObserver = new ResizeObserver((entries) => {
            this.geometry_changed()
            this.display_if_needed()
        });
        resizeObserver.observe(this.el);

        document.addEventListener("scroll", (event) => {
            if (!this.awaiting_scroll_event) {
                this.awaiting_scroll_event = true;
                window.requestAnimationFrame(() => {
                    this.awaiting_scroll_event = false;
                    this.did_scroll();
                });
            }
        });

        const observer = new IntersectionObserver((entries, observer) => {
            this.onscreen_promise.mark_completed()
            observer.disconnect()
        }, { rootMargin: "0px", threshold: 0.5, });
        observer.observe(this.el);
        
        const tick_throttle_observer = new IntersectionObserver((entries, observer) => {
            this.note_onscreen(entries[0].isIntersecting)
        }, { rootMargin: "0px", threshold: 0.0, });
        tick_throttle_observer.observe(this.el);
 
        this.queue_tick_if_required()

        this.do_run()
    }

    private async do_run(): Promise<void> {
        await this.handle_run()
        this.display_if_needed()
    }
}

function asyncImageLoader(url: string): Promise<HTMLImageElement> {
    return new Promise( (resolve, reject) => {
        var image = new Image()
        image.src = url
        image.onload = () => resolve(image)
        image.onerror = () => reject(new Error('could not load image ' + url))
    })
}    

export type Sprite = {
    i: number,
    j: number,
    w: number,
    h: number,
    source_dimension: number,
    atlas: HTMLImageElement,
}

export class SpriteAtlas {
    private source_dimension: number
    private atlas: HTMLImageElement

    constructor(source_dimension: number, atlas: HTMLImageElement) {
        this.source_dimension = source_dimension
        this.atlas = atlas
    }

    public create_sprite(x: number, y: number, w: number, h: number): Sprite {
        return {
            i: x,
            j: y,
            w: w,
            h: h,
            source_dimension: this.source_dimension,
            atlas: this.atlas,
        }
    }
}

export class DrawContext {
    private ctx: CanvasRenderingContext2D

    constructor(ctx: CanvasRenderingContext2D) {
        this.ctx = ctx
    }

    public save(): void {
        this.ctx.save()
    }

    public restore(): void {
        this.ctx.restore()
    }

    public translate(dx: number, dy: number): void {
        this.ctx.translate(dx, dy)
    }

    public rotate(degrees: number): void {
        this.ctx.rotate(degrees * Math.PI / 180.0)
    }

    public scale(sx: number, sy: number): void {
        this.ctx.scale(sx, sy)
    }

    public alpha(opacity: number): void {
        this.ctx.globalAlpha = opacity
    }

    public tile_line(width: number, l: Sprite, c: Sprite, r: Sprite): void {
        // tiling seems to leave gaps (on firefox at least), scaling up v slightly fixes them
        const sw:number = 1.05

        this.save()

        this.save()
        this.scale(sw, 1.0)
        this.draw_sprite(l)
        this.restore()

        for (var i = 0; i < width - 2; i += 1) {
            this.translate(1, 0)

            this.save()
            this.scale(sw, 1.0)
            this.draw_sprite(c)
            this.restore()
        }
        this.translate(1, 0)
        this.save()
        this.scale(sw, 1.0)
        this.draw_sprite(r)
        this.restore()
        this.restore()
    }

    public clip_rect(w: number, h: number): void {
        /*
          My first try at this added the rect to the path, then clipped to that
          For some reason it gvave junk results on Firefox at that time
         */
        let path = new Path2D()
        path.rect(0, 0, w, h)
        this.ctx.clip(path)
    }

    public draw_sprite(s: Sprite): void {
        this.ctx.save()
        this.ctx.translate(-0.5 * s.w, -0.5 * s.h)
        this.ctx.drawImage(
            s.atlas,
            s.i * s.source_dimension,
            s.j * s.source_dimension,
            s.w * s.source_dimension,
            s.h * s.source_dimension,
            0,
            0,
            s.w,
            s.h
        )
        this.ctx.restore()
    }

    public canvas_context(): CanvasRenderingContext2D {
        return this.ctx
    }
}

export class CanvasEngine extends EngineBase {
    private canvas: HTMLCanvasElement
    private ctx: DrawContext
    private background_colour: string
    private needs_display: boolean

    // stubs
    protected handle_render(ctx: DrawContext): void { }

    constructor(canvas_name: string) {
        let canvas = document.getElementById(canvas_name) as HTMLCanvasElement
        super(canvas)
        this.canvas = canvas
        this.background_colour = "ffffff"
        this.needs_display = true
        this.ctx = new DrawContext(this.canvas.getContext("2d")!)
    }

    // force redisplay
    protected set_needs_display(): void {
        this.needs_display = true
    }

    protected display_if_needed(): void {
        if (this.needs_display) {
            this.needs_display = false
            this.render()
        }
    }

    protected geometry_changed(): void {
        super.geometry_changed()
        this.set_needs_display()
    }

    protected async load_atlas(name: string, dimension: number): Promise<SpriteAtlas> {
        let atlas = await asyncImageLoader(name)
        return new SpriteAtlas(dimension, atlas)
    }

    public async setup(): Promise<void> {
        this.set_needs_display()
        await super.setup()
    }

    protected set_background(col: string): void {
        this.background_colour = col
        this.set_needs_display()
    }

    private render(): void {
        let ctx = this.ctx.canvas_context()
        ctx.resetTransform()

        this.canvas.height = Math.floor(this.canvas.offsetHeight * window.devicePixelRatio) * 2
        this.canvas.width = Math.floor(this.canvas.offsetWidth * window.devicePixelRatio) * 2

        ctx.scale(this.canvas.width / this.width(), this.canvas.height / this.height())
 
        ctx.fillStyle = this.background_colour
        ctx.fillRect(0, 0, this.width(), this.height())

        this.handle_render(this.ctx)
    }
}

